import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { Badge, Drawer, List, Typography, Tag, Empty, Button, Tooltip, Tabs } from "antd";
import { 
  BellOutlined, RightOutlined, ClockCircleOutlined, CalendarOutlined,
  UnlockOutlined, CheckCircleOutlined, SwapOutlined, TeamOutlined,
  HistoryOutlined, NotificationOutlined, TagOutlined, LogoutOutlined,
  FundProjectionScreenOutlined, FileDoneOutlined, SyncOutlined, DollarOutlined,
  CheckOutlined, DeleteOutlined, InboxOutlined, GiftOutlined, AuditOutlined,
  InfoCircleOutlined, CheckSquareOutlined
} from "@ant-design/icons";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import axios from "axios";
import { useNavigation } from "../context/NavigationContext";

dayjs.extend(relativeTime);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

/* ─────────────────────────────────────────────────────────────────
   ROBUST LINK RESOLUTION WITH ROLE-BASED ROUTING
────────────────────────────────────────────────────────────────── */

function extractIdFromLink(link) {
  if (!link || typeof link !== "string") return null;
  const match = link.match(/\/(\d+)\/?$/);
  return match ? match[1] : null;
}

function resolveLink(link, message = "", authData = null) {
  if (!link || typeof link !== "string") return null;
  const normalized = link.toLowerCase().trim();
  const msgLower = (message || "").toLowerCase();
  const id = extractIdFromLink(link);

  const isApprovalRequest = msgLower.includes("action required") ||
    (msgLower.includes("approve") && !msgLower.includes("approved")) ||
    msgLower.includes("waiting for your approval") ||
    msgLower.includes("pending your review") ||
    msgLower.includes("request raised") ||
    (msgLower.includes("new") && msgLower.includes("request"));

  // LEAVE
  if (normalized.includes("leave")) {
    const targetStatus = msgLower.includes("approved") ? "Approved" : (msgLower.includes("rejected") ? "Rejected" : "Pending");
    if (isApprovalRequest) return { menuKey: "leaveRequests", params: { leaveId: id, openModal: true, status: "Pending" } };
    return { menuKey: "leaves", params: { leaveId: id, openModal: true, status: targetStatus } };
  }
  // PERMISSION
  if (normalized.includes("permission")) {
    const targetStatus = msgLower.includes("approved") ? "Approved" : (msgLower.includes("rejected") ? "Rejected" : "Pending");
    if (isApprovalRequest) return { menuKey: "permissionRequests", params: { permissionId: id, openModal: true, status: "Pending" } };
    return { menuKey: "permissions", params: { permissionId: id, openModal: true, status: targetStatus } };
  }
  // ON-DUTY
  if (normalized.includes("on-duty") || normalized.includes("onduty")) {
    const targetStatus = msgLower.includes("approved") ? "Approved" : (msgLower.includes("rejected") ? "Rejected" : "Pending");
    if (isApprovalRequest) return { menuKey: "onduty", params: { status: "Pending" } };
    return { menuKey: "appliedOnDuty", params: { status: targetStatus } };
  }
  // TRANSFER
  if (normalized.includes("transfer") || normalized.includes("/payroll/super")) {
    if (msgLower.includes("you have been transferred") || msgLower.includes("your transfer")) {
      return { menuKey: "profile", params: null };
    }
    const targetStatus = msgLower.includes("approved") ? "APPROVED" : (msgLower.includes("rejected") ? "REJECTED" : "PENDING");
    return { menuKey: "empTransfer", params: { status: targetStatus } };
  }
  // RFC
  if (normalized.includes("rfc")) {
    const targetStatus = msgLower.includes("approved") ? "Approved" : "Pending";
    return { menuKey: authData?.employee?.is_hr ? "rfc" : "myrfc", params: { status: targetStatus } };
  }
  // TICKET
  if (normalized.includes("ticket")) {
    return { menuKey: isApprovalRequest ? "tickets" : "viewTicket", params: { status: "NEW" } };
  }
  // PROFILE
  if (normalized.includes("/profile")) return { menuKey: "profile", params: null };

  return null;
}

function getTagProps(link, message = "") {
  const normalized = (link || "").toLowerCase();
  const msgLower = (message || "").toLowerCase();
  if (normalized.includes("leave")) return { color: "#10b981", bg: "#ecfdf5", label: "Leave", icon: <CalendarOutlined /> };
  if (normalized.includes("permission")) return { color: "#8b5cf6", bg: "#f5f3ff", label: "Permission", icon: <UnlockOutlined /> };
  if (normalized.includes("transfer")) return { color: "#f97316", bg: "#fff7ed", label: "Transfer", icon: <SwapOutlined /> };
  if (normalized.includes("payroll") || msgLower.includes("salary")) return { color: "#059669", bg: "#ecfdf5", label: "Payroll", icon: <DollarOutlined /> };
  if (msgLower.includes("birthday") || msgLower.includes("wish")) return { color: "#ec4899", bg: "#fdf2f8", label: "Celebration", icon: <GiftOutlined /> };
  return { color: "#2563eb", bg: "#eff6ff", label: "System", icon: <BellOutlined /> };
}

const NotificationBar = ({ backUrl, authData }) => {
  const [notifications, setNotifications] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const { navigateTo } = useNavigation();
  const headers = useMemo(() => ({ Authorization: `Bearer ${authData?.access}` }), [authData]);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${backUrl}/api/probation/notifications/?t=${Date.now()}`, { headers });
      setNotifications(res.data?.results || res.data || []);
    } catch (error) { console.error(error); }
  }, [backUrl, headers]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    try {
      await axios.post(`${backUrl}/api/probation/notifications/${id}/mark-as-read/`, {}, { headers });
    } catch (err) { console.error(err); }
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
    if (unreadIds.length === 0) return;
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    try {
      await Promise.all(unreadIds.map(id => axios.post(`${backUrl}/api/probation/notifications/${id}/mark-as-read/`, {}, { headers })));
    } catch (err) { console.error(err); }
  };

  const handleNotificationClick = async (item) => {
    if (!item.is_read) await markAsRead(item.id);
    const resolved = resolveLink(item.link, item.message, authData);
    if (!resolved?.menuKey) { setDrawerVisible(false); return; }

    const currentPath = window.location.pathname.toLowerCase();
    let targetDashboardUrl = "/employee";
    const isHrUser = authData?.employee?.is_hr || authData?.employee?.is_superuser;
    const isHodUser = authData?.employee?.is_hod || authData?.employee?.team_count > 0;
    const msgLower = (item.message || "").toLowerCase();
    const linkStr = (item.link || "").toLowerCase();

    if (isHrUser && (linkStr.includes("/hr/") || linkStr.includes("/payroll/"))) {
      targetDashboardUrl = linkStr.includes("/payroll/") ? "/payroll_hr" : "/hrdashboard";
    } else if (isHodUser && (linkStr.includes("/hod/") || msgLower.includes("approval"))) {
      targetDashboardUrl = "/hod";
    }

    if (resolved.menuKey === "profile" || resolved.menuKey === "home") {
      if (currentPath.includes("/hrdashboard")) targetDashboardUrl = "/hrdashboard";
      else if (currentPath.includes("/hod")) targetDashboardUrl = "/hod";
      else if (currentPath.includes("/payroll_hr")) targetDashboardUrl = "/payroll_hr";
    }

    if (targetDashboardUrl && !currentPath.includes(targetDashboardUrl)) {
      if (resolved.params) localStorage.setItem("bemss_navigation_params", JSON.stringify(resolved.params));
      const dashboardKey = targetDashboardUrl === "/hrdashboard" ? "hrDashboardPage" : (targetDashboardUrl === "/hod" ? "hodDashboardPage" : (targetDashboardUrl === "/payroll_hr" ? "payrollHrPage" : "employeePage"));
      localStorage.setItem(dashboardKey, resolved.menuKey);
      window.location.href = targetDashboardUrl;
    } else {
      setDrawerVisible(false);
      navigateTo(resolved.menuKey, resolved.params);
    }
  };

  const groupedNotifications = useMemo(() => {
    const list = activeTab === "unread" ? notifications.filter(n => !n.is_read) : notifications;
    const groups = { today: [], yesterday: [], earlier: [] };
    list.forEach(n => {
      const d = dayjs(n.created_at);
      if (d.isToday()) groups.today.push(n);
      else if (d.isYesterday()) groups.yesterday.push(n);
      else groups.earlier.push(n);
    });
    return groups;
  }, [notifications, activeTab]);

  const renderList = (items) => (
    <List
      dataSource={items}
      split={false}
      renderItem={(item) => {
        const tagProps = getTagProps(item.link, item.message);
        return (
          <motion.div
            className="premium-notif-card"
            whileHover={{ y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => handleNotificationClick(item)}
            style={{
              padding: "14px 16px 14px 12px",
              marginBottom: "10px",
              borderRadius: "14px",
              cursor: "pointer",
              backgroundColor: item.is_read ? "#ffffff" : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              border: item.is_read ? "1px solid #e2e8f0" : "1px solid rgba(37, 99, 235, 0.25)",
              boxShadow: item.is_read ? "0 2px 6px rgba(15,23,42,0.03)" : "0 6px 18px rgba(37, 99, 235, 0.08)",
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: "12px"
            }}
          >
            {/* Left Accent Color Indicator Bar */}
            <div
              style={{
                width: 4,
                height: 40,
                borderRadius: 4,
                background: item.is_read ? "transparent" : tagProps.color,
                flexShrink: 0
              }}
            />

            {/* Category Icon */}
            <div
              style={{
                width: 42,
                height: 42,
                borderRadius: 12,
                background: tagProps.bg,
                color: tagProps.color,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 19,
                flexShrink: 0,
                boxShadow: `0 4px 12px ${tagProps.color}22`
              }}
            >
              {tagProps.icon}
            </div>

            {/* Content Body */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 800,
                    color: tagProps.color,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px"
                  }}
                >
                  {tagProps.label}
                </span>
                <span style={{ fontSize: 10.5, color: "#94a3b8", fontWeight: 600 }}>
                  {dayjs(item.created_at).fromNow()}
                </span>
              </div>

              <div
                style={{
                  fontSize: 13,
                  color: item.is_read ? "#64748b" : "#0f172a",
                  lineHeight: 1.45,
                  fontWeight: item.is_read ? 500 : 700
                }}
              >
                {item.message}
              </div>
            </div>

            {/* Right Arrow Navigation Indicator */}
            <RightOutlined style={{ fontSize: 12, color: "#cbd5e1", flexShrink: 0 }} />
          </motion.div>
        );
      }}
    />
  );

  const [currentMenu, setCurrentMenu] = useState(() => localStorage.getItem("hrDashboardPage") || "home");

  useEffect(() => {
    const checkMenu = () => {
      const page = localStorage.getItem("hrDashboardPage") || "home";
      setCurrentMenu(page);
    };
    checkMenu();
    window.addEventListener("storage", checkMenu);
    const interval = setInterval(checkMenu, 250);
    return () => {
      window.removeEventListener("storage", checkMenu);
      clearInterval(interval);
    };
  }, []);

  const isDashboard = currentMenu === "home";

  return (
    <>
      <style>{`
        .premium-notif-card {
          padding: 14px 16px;
          margin-bottom: 10px;
          border-radius: 14px;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          display: flex;
          gap: 14px;
          backdrop-filter: blur(10px);
        }
        .premium-notif-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08) !important;
          border-color: rgba(37, 99, 235, 0.3) !important;
        }
        .glass-drawer .ant-drawer-content {
          background: rgba(255, 255, 255, 0.94) !important;
          backdrop-filter: blur(25px) !important;
        }
        .glass-drawer .ant-drawer-header {
          background: #ffffff !important;
          border-bottom: 1px solid #e2e8f0 !important;
          padding: 16px 24px !important;
        }
        .glass-drawer .ant-drawer-body {
          padding: 16px 24px !important;
        }
        .section-label {
          font-size: 11px;
          font-weight: 800;
          color: #64748b;
          text-transform: uppercase;
          margin: 18px 0 8px 2px;
          display: flex;
          align-items: center;
          gap: 6px;
          letter-spacing: 1.2px;
        }
        .header-bell-trigger {
          width: 38px;
          height: 38px;
          border-radius: 12px;
          background: #ffffff;
          border: 1px solid #e2e8f0;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
          transition: all 0.22s ease;
        }
        .header-bell-trigger:hover {
          background: #f8fafc;
          border-color: #cbd5e1;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(14, 73, 123, 0.12);
        }
      `}</style>

      {/* HEADER NAVBAR BELL ICON TRIGGER */}
      <Tooltip title="Notification Center">
        <div className="header-bell-trigger" onClick={() => setDrawerVisible(true)}>
          <Badge count={notifications.filter(n => !n.is_read).length} size="small" offset={[2, -2]} style={{ backgroundColor: "#ef4444", fontWeight: 800 }}>
            <BellOutlined style={{ fontSize: 18, color: "#0e497b" }} />
          </Badge>
        </div>
      </Tooltip>

      {/* FLOATING ACTION BELL BUTTON FOR DASHBOARD */}
      {isDashboard && createPortal(
        <Tooltip title="Notification Center">
          <div
            style={{
              position: "fixed",
              bottom: 30,
              right: 30,
              zIndex: 9999,
              background: "linear-gradient(135deg, #0e497b 0%, #1d4ed8 100%)",
              color: "white",
              width: 50,
              height: 50,
              borderRadius: 16,
              boxShadow: "0 10px 28px rgba(14,73,123,0.38)",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.25s ease",
            }}
            onClick={() => setDrawerVisible(true)}
            onMouseEnter={e => e.currentTarget.style.transform = "translateY(-3px) scale(1.04)"}
            onMouseLeave={e => e.currentTarget.style.transform = "translateY(0) scale(1)"}
          >
            <Badge count={notifications.filter(n => !n.is_read).length} size="small" offset={[-2, 2]} style={{ backgroundColor: "#ef4444" }}>
              <BellOutlined style={{ fontSize: 20, color: "white" }} />
            </Badge>
          </div>
        </Tooltip>,
        document.body
      )}

      {/* EXECUTIVE NOTIFICATION DRAWER SCREEN */}
      <Drawer
        className="glass-drawer"
        title={
          <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "rgba(37, 99, 235, 0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "#2563eb" }}>
                <BellOutlined style={{ fontSize: 17 }} />
              </div>
              <div>
                <Typography.Title level={4} style={{ margin: 0, fontSize: 16, fontWeight: 900, color: "#0f172a" }}>
                  Notifications
                </Typography.Title>
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700 }}>
                  Stay updated with live company alerts
                </div>
              </div>
            </div>
            {notifications.some(n => !n.is_read) && (
              <Button type="text" size="small" onClick={markAllAsRead} style={{ color: "#2563eb", fontWeight: 800, fontSize: 12 }}>
                Mark all as read
              </Button>
            )}
          </div>
        }
        placement="right"
        width={420}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: "all", label: `All (${notifications.length})` },
            { key: "unread", label: `New (${notifications.filter(n => !n.is_read).length})` }
          ]}
        />

        {notifications.length === 0 ? (
          <Empty style={{ marginTop: 100 }} description={<span style={{ fontWeight: 700, color: "#64748b" }}>You're all caught up! ✨</span>} />
        ) : (
          <div style={{ paddingBottom: 30 }}>
            {groupedNotifications.today.length > 0 && <><span className="section-label">📌 Today</span>{renderList(groupedNotifications.today)}</>}
            {groupedNotifications.yesterday.length > 0 && <><span className="section-label">📅 Yesterday</span>{renderList(groupedNotifications.yesterday)}</>}
            {groupedNotifications.earlier.length > 0 && <><span className="section-label">⏳ Earlier</span>{renderList(groupedNotifications.earlier)}</>}
          </div>
        )}
      </Drawer>
    </>
  );
};

export default NotificationBar;
